 // src/components/admin/PromoCodeDetail.tsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  ChevronLeft, 
  Edit, 
  Save,
  CheckCircle, 
  XCircle, 
  Users, 
  RefreshCw,
  Clock,
  Tag,
  Info,
  AlertTriangle,
  PieChart
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { PromoCode, RedemptionHistoryItem } from '../../types/admin';

interface PromoCodeDetailProps {
  promoCode: PromoCode;
  onBack: () => void;
  onUpdate: (updatedCode: PromoCode) => void;
}

const PromoCodeDetail: React.FC<PromoCodeDetailProps> = ({
  promoCode: initialPromoCode,
  onBack,
  onUpdate
}) => {
  // State for promo code data
  const [promoCode, setPromoCode] = useState<PromoCode>(initialPromoCode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Redemption history
  const [redemptionHistory, setRedemptionHistory] = useState<RedemptionHistoryItem[]>([]);
  const [redemptionStats, setRedemptionStats] = useState<{
    totalRedemptions: number;
    activeSubscriptions: number;
    expiredSubscriptions: number;
    conversionRate: number;
  }>({
    totalRedemptions: 0,
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    conversionRate: 0
  });
  
  // Edit mode for fields that can be modified
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableFields, setEditableFields] = useState({
    isActive: promoCode.isActive,
    expiresAt: promoCode.expiresAt || '',
    maxRedemptions: promoCode.maxRedemptions || '',
    description: promoCode.description || ''
  });
  
  // Activity log for actions taken on this promo code
  const [activityLog, setActivityLog] = useState<{
    action: string;
    timestamp: string;
    user?: string;
  }[]>([]);
  
  // Fetch detailed promo code data
  const fetchPromoCodeDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get detailed promo code data
      const detailedCode = await adminApi.getPromoCode(promoCode.id);
      setPromoCode(detailedCode);
      
      // Set editable fields from the new data
      setEditableFields({
        isActive: detailedCode.isActive,
        expiresAt: detailedCode.expiresAt || '',
        maxRedemptions: detailedCode.maxRedemptions || '',
        description: detailedCode.description || ''
      });
      
      // Get redemption history
      const history = await adminApi.getRedemptionHistory(detailedCode.code);
      setRedemptionHistory(history);
      
      // Get redemption stats
      const stats = await adminApi.getRedemptionStats(detailedCode.code);
      setRedemptionStats(stats);
      
      // For the sake of demonstration, we'll create a simulated activity log
      // In a real app, this would come from the API
      setActivityLog([
        {
          action: 'Promo code created',
          timestamp: detailedCode.createdAt,
          user: 'Admin'
        },
        {
          action: `${detailedCode.redemptionCount} redemptions recorded`,
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch promo code details');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch data on mount
  useEffect(() => {
    fetchPromoCodeDetails();
  }, [promoCode.id]);
  
  // Handle field changes
  const handleFieldChange = (
    field: keyof typeof editableFields,
    value: string | boolean | number
  ) => {
    setEditableFields({
      ...editableFields,
      [field]: value
    });
  };
  
  // Handle save
  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Prepare data for API
      const updateData = {
        isActive: editableFields.isActive,
        expiresAt: editableFields.expiresAt || null,
        maxRedemptions: editableFields.maxRedemptions ? Number(editableFields.maxRedemptions) : null,
        description: editableFields.description
      };
      
      // Update via API
      const updatedCode = await adminApi.updatePromoCode(promoCode.id, updateData);
      
      // Update local state
      setPromoCode(updatedCode);
      
      // Exit edit mode
      setIsEditMode(false);
      
      // Call update callback
      onUpdate(updatedCode);
      
      // Add to activity log
      setActivityLog([
        {
          action: 'Promo code updated',
          timestamp: new Date().toISOString(),
          user: 'Admin'
        },
        ...activityLog
      ]);
    } catch (err: any) {
      setError(err.message || 'Failed to update promo code');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle toggle active status
  const handleToggleActive = async () => {
    const newStatus = !promoCode.isActive;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update via API
      const updatedCode = await adminApi.updatePromoCode(promoCode.id, {
        isActive: newStatus
      });
      
      // Update local state
      setPromoCode(updatedCode);
      setEditableFields({
        ...editableFields,
        isActive: newStatus
      });
      
      // Call update callback
      onUpdate(updatedCode);
      
      // Add to activity log
      setActivityLog([
        {
          action: newStatus ? 'Promo code activated' : 'Promo code deactivated',
          timestamp: new Date().toISOString(),
          user: 'Admin'
        },
        ...activityLog
      ]);
    } catch (err: any) {
      setError(err.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} promo code`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Render status badge
  const renderStatusBadge = () => {
    if (!promoCode.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          <XCircle className="w-4 h-4 mr-1" />
          Inactive
        </span>
      );
    }
    
    if (promoCode.expiresAt && new Date(promoCode.expiresAt) < new Date()) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <Calendar className="w-4 h-4 mr-1" />
          Expired
        </span>
      );
    }
    
    if (promoCode.maxRedemptions && promoCode.redemptionCount >= promoCode.maxRedemptions) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <XCircle className="w-4 h-4 mr-1" />
          Fully Redeemed
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-4 h-4 mr-1" />
        Active
      </span>
    );
  };
  
  return (
    <div>
      {/* Header with back button */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to List
        </button>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={`px-4 py-2 rounded-md ${
              isEditMode ? 'bg-gray-200 text-gray-800' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isEditMode ? 'Cancel Edit' : 'Edit Code'}
          </button>
          <button
            onClick={handleToggleActive}
            className={`px-4 py-2 rounded-md ${
              promoCode.isActive ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
            }`}
            disabled={isLoading}
          >
            {promoCode.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          {error}
        </div>
      )}
      
      {/* Main content grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Promo code details panel */}
        <div className="md:col-span-2 space-y-6">
          {/* Main details card */}
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800 font-mono">{promoCode.code}</h2>
              {renderStatusBadge()}
            </div>
            
            {/* Details grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-sm text-gray-500">Plan Type</div>
                <div className="text-lg font-medium capitalize">{promoCode.planType}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Duration Type</div>
                <div className="text-lg font-medium capitalize">{promoCode.durationType}</div>
              </div>
              {promoCode.durationType !== 'forever' && promoCode.durationMonths && (
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div className="text-lg font-medium">
                    {promoCode.durationMonths} month{promoCode.durationMonths !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm text-gray-500">Created</div>
                <div className="text-lg font-medium">
                  {new Date(promoCode.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Redemptions</div>
                <div className="text-lg font-medium">
                  {promoCode.redemptionCount} 
                  {promoCode.maxRedemptions ? 
                    ` / ${promoCode.maxRedemptions}` : 
                    ''}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Created By</div>
                <div className="text-lg font-medium">
                  {promoCode.creator?.name || 'Admin'}
                </div>
              </div>
            </div>
            
            {/* Editable fields section */}
            {isEditMode ? (
              <div className="border-t pt-4">
                <h3 className="text-md font-medium mb-4">Edit Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editableFields.isActive ? 'active' : 'inactive'}
                      onChange={e => handleFieldChange('isActive', e.target.value === 'active')}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={editableFields.expiresAt ? new Date(editableFields.expiresAt).toISOString().split('T')[0] : ''}
                      onChange={e => handleFieldChange('expiresAt', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Redemptions
                    </label>
                    <input
                      type="number"
                      value={editableFields.maxRedemptions}
                      onChange={e => handleFieldChange('maxRedemptions', e.target.value)}
                      min="0"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editableFields.description}
                      onChange={e => handleFieldChange('description', e.target.value)}
                      rows={3}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    disabled={isLoading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <h3 className="text-md font-medium mb-2">Additional Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {promoCode.expiresAt && (
                    <div>
                      <div className="text-sm text-gray-500">Expires</div>
                      <div className="text-lg font-medium">
                        {new Date(promoCode.expiresAt).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                  {promoCode.description && (
                    <div className="md:col-span-2">
                      <div className="text-sm text-gray-500">Description</div>
                      <div className="text-md text-gray-700 mt-1">
                        {promoCode.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Redemption History */}
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Redemption History</h3>
              <button
                onClick={fetchPromoCodeDetails}
                className="text-blue-600 hover:text-blue-800"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : redemptionHistory.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                No redemptions yet
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Redeemed At
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Converted
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {redemptionHistory.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {item.userName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(item.redeemedAt).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(item.redeemedAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 capitalize">
                            {item.planType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 capitalize">
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.convertedToPaid ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <XCircle className="h-3 w-3 mr-1" />
                              No
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        
        {/* Statistics panel */}
        <div className="space-y-6">
          {/* Key metrics */}
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium mb-4 flex items-center">
              <PieChart className="h-5 w-5 text-blue-600 mr-2" />
              Performance Metrics
            </h3>
            
            <div className="space-y-4">
              <div className="border-b pb-3">
                <div className="text-sm text-gray-500">Total Redemptions</div>
                <div className="text-3xl font-bold text-gray-900">
                  {redemptionStats.totalRedemptions}
                </div>
              </div>
              
              <div className="border-b pb-3">
                <div className="text-sm text-gray-500">Active Subscriptions</div>
                <div className="text-3xl font-bold text-green-600">
                  {redemptionStats.activeSubscriptions}
                </div>
              </div>
              
              <div className="border-b pb-3">
                <div className="text-sm text-gray-500">Expired Subscriptions</div>
                <div className="text-3xl font-bold text-gray-500">
                  {redemptionStats.expiredSubscriptions}
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500">Conversion Rate</div>
                <div className="text-3xl font-bold text-blue-600">
                  {(redemptionStats.conversionRate * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Percentage of code users who converted to paid subscriptions
                </div>
              </div>
            </div>
          </div>
          
          {/* Activity Log */}
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium mb-4 flex items-center">
              <Clock className="h-5 w-5 text-blue-600 mr-2" />
              Activity Log
            </h3>
            
            <div className="space-y-3">
              {activityLog.map((activity, index) => (
                <div key={index} className="flex items-start py-2 border-b border-gray-100 last:border-b-0">
                  <div className={`h-4 w-4 rounded-full mt-1 mr-3 flex-shrink-0 
                    ${activity.action.includes('created') ? 'bg-green-500' : 
                      activity.action.includes('updated') ? 'bg-blue-500' : 
                      activity.action.includes('deactivated') ? 'bg-yellow-500' : 
                      'bg-gray-500'}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {activity.action}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(activity.timestamp).toLocaleString()} 
                      {activity.user && ` by ${activity.user}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Usage Tips */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium mb-4 flex items-center text-blue-800">
              <Info className="h-5 w-5 text-blue-600 mr-2" />
              Tips
            </h3>
            
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0" />
                <span>Edit this promo code to update its expiration date or maximum redemptions</span>
              </li>
              <li className="flex items-start">
                <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0" />
                <span>Deactivate the code to prevent new users from redeeming it</span>
              </li>
              <li className="flex items-start">
                <div className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0" />
                <span>Check redemption history to see who has used this code</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeDetail;